// Every social report template. Template ids and types must match
// App\Services\SocialReports\ReportTemplates, which also sets each type's default (DEFAULTS).
import { badgeFields, type FieldDef, type TemplateDef } from './context'
import { statFields } from './stats'
import type { ReportData, ReportType } from './types'
import ActivityAnalysis from './templates/ActivityAnalysis.svelte'
import ActivityPoster from './templates/ActivityPoster.svelte'
import ActivityRoute from './templates/ActivityRoute.svelte'
import BtPoster from './templates/BtPoster.svelte'
import BtSignature from './templates/BtSignature.svelte'
import BtSpotlight from './templates/BtSpotlight.svelte'
import GroupLeaderboard from './templates/GroupLeaderboard.svelte'
import GroupPeloton from './templates/GroupPeloton.svelte'
import WorkoutCard from './templates/WorkoutCard.svelte'
import WorkoutIntervals from './templates/WorkoutIntervals.svelte'
import XfaiPoster from './templates/XfaiPoster.svelte'
import XfaiProgress from './templates/XfaiProgress.svelte'

const hasPower = (d: ReportData) => !!d.series?.power
const hasMpa = (d: ReportData) => !!d.series?.mpa
const hasRoute = (d: ReportData) => !!d.route
const hasAltitude = (d: ReportData) => !!d.series?.alt

function basics({ title = true, athlete = false } = {}): FieldDef[] {
  return [
    { key: 'title', label: 'Activity name', group: 'Content', default: title },
    { key: 'date', label: 'Date', group: 'Content' },
    { key: 'athlete', label: 'Your name', group: 'Content', default: athlete },
  ]
}

const chart = (label = 'Power chart'): FieldDef => ({ key: 'chart', label, group: 'Graphics', available: hasPower })
const mpa: FieldDef = { key: 'mpa', label: 'MPA line', group: 'Graphics', default: false, available: hasMpa }
const route: FieldDef = { key: 'route', label: 'Route map', group: 'Graphics', available: hasRoute }

export const TEMPLATES: TemplateDef[] = [
  {
    id: 'bt-spotlight',
    type: 'breakthrough',
    name: 'Spotlight',
    description: 'Your medal, signature gains and the moment of the breakthrough.',
    component: BtSpotlight,
    defaultBackground: 'none',
    fields: [
      ...basics(),
      { key: 'deltas', label: 'Signature changes', group: 'Content' },
      ...statFields(['stat.xss', 'stat.difficulty', 'stat.time', 'stat.distance', 'stat.power', 'stat.recovery'], ['stat.power', 'stat.recovery']),
      chart(),
      mpa,
      { key: 'marker', label: 'Breakthrough marker', group: 'Graphics', available: hasPower },
      ...badgeFields('breakthrough'),
    ],
  },
  {
    id: 'bt-poster',
    type: 'breakthrough',
    name: 'Poster',
    description: 'A photo with your biggest signature gain as the headline.',
    component: BtPoster,
    defaultBackground: 'photo',
    fields: [
      ...basics(),
      { key: 'delta.tp', label: 'Threshold Power change', group: 'Content' },
      { key: 'delta.hie', label: 'High Intensity Energy change', group: 'Content' },
      { key: 'delta.pp', label: 'Peak Power change', group: 'Content' },
      ...statFields(['stat.xss', 'stat.time', 'stat.distance', 'stat.difficulty', 'stat.recovery'], ['stat.difficulty', 'stat.recovery']),
      chart('Power line'),
      ...badgeFields('breakthrough'),
    ],
  },
  {
    id: 'bt-signature',
    type: 'breakthrough',
    name: 'Signature',
    description: 'A clean, light sheet of your new fitness signature.',
    component: BtSignature,
    defaultBackground: 'none',
    defaultAccent: 'orange',
    fields: [
      ...basics(),
      { key: 'bars', label: 'Before / after', group: 'Content' },
      ...statFields(['stat.xss', 'stat.difficulty', 'stat.time', 'stat.distance', 'stat.xep', 'stat.recovery'], ['stat.xep', 'stat.recovery']),
      chart(),
      mpa,
      ...badgeFields('breakthrough'),
    ],
  },
  {
    id: 'xfai-progress',
    type: 'xfai',
    name: 'Progress',
    description: 'Plan progress, today’s targets vs what you did, and training status.',
    component: XfaiProgress,
    defaultBackground: 'none',
    defaultAccent: 'purple',
    fields: [
      { key: 'plan', label: 'Plan name & focus', group: 'Content' },
      { key: 'progress', label: 'Plan day', group: 'Graphics', available: (d) => !!(d.xfai?.day && d.xfai?.days_total) },
      { key: 'targets', label: 'XSS targets', group: 'Graphics' },
      { key: 'status', label: 'Training status', group: 'Content', available: (d) => !!d.xfai?.status.label },
      { key: 'recovery', label: 'Recovery days', group: 'Content' },
      ...basics(),
      ...statFields(['stat.xss', 'stat.difficulty', 'stat.time', 'stat.distance', 'stat.xep'], ['stat.xss', 'stat.xep']),
      chart(),
      ...badgeFields('xfai'),
    ],
  },
  {
    id: 'xfai-poster',
    type: 'xfai',
    name: 'Poster',
    description: 'A photo headlined by your plan day and today’s result.',
    component: XfaiPoster,
    defaultBackground: 'photo',
    defaultAccent: 'purple',
    fields: [
      { key: 'plan', label: 'Plan name & focus', group: 'Content' },
      { key: 'progress', label: 'Plan day', group: 'Graphics', available: (d) => !!(d.xfai?.day && d.xfai?.days_total) },
      { key: 'verdict', label: 'Result (e.g. Target hit)', group: 'Content' },
      { key: 'targets', label: 'XSS targets', group: 'Graphics', default: false },
      ...basics(),
      ...statFields(['stat.time', 'stat.distance', 'stat.difficulty', 'stat.xep', 'stat.recovery'], ['stat.xep', 'stat.recovery']),
      ...badgeFields('xfai'),
    ],
  },
  {
    id: 'workout-intervals',
    type: 'workout',
    name: 'Intervals',
    description: 'The workout’s targets against what you rode.',
    component: WorkoutIntervals,
    defaultBackground: 'none',
    fields: [
      { key: 'program', label: 'Program day', group: 'Content', available: (d) => !!d.workout?.program },
      { key: 'difficulty', label: 'Difficulty rating', group: 'Content', available: (d) => !!d.activity.difficulty_stars },
      { key: 'focus', label: 'Focus', group: 'Content', available: (d) => !!d.activity.focus },
      ...basics({ title: false }),
      ...statFields(['stat.xss', 'stat.time', 'stat.xep', 'stat.power', 'stat.distance', 'stat.hr', 'stat.recovery'], ['stat.power', 'stat.distance', 'stat.hr', 'stat.recovery']),
      chart(),
      { key: 'targets', label: 'Target power', group: 'Graphics', available: (d) => !!d.workout?.has_targets },
      ...badgeFields('workout'),
    ],
  },
  {
    id: 'workout-card',
    type: 'workout',
    name: 'Complete',
    description: 'A bold “workout complete” card with your big numbers.',
    component: WorkoutCard,
    defaultBackground: 'none',
    defaultAccent: 'orange',
    fields: [
      { key: 'difficulty', label: 'Difficulty rating', group: 'Content', available: (d) => !!d.activity.difficulty_stars },
      ...basics({ title: false }),
      ...statFields(['stat.xss', 'stat.time', 'stat.xep', 'stat.focus', 'stat.power', 'stat.carbs'], ['stat.power', 'stat.carbs']),
      chart(),
      ...badgeFields('workout'),
    ],
  },
  {
    id: 'group-peloton',
    type: 'group',
    name: 'Peloton',
    description: 'Who you rode with, the group’s pulling power and the route.',
    component: GroupPeloton,
    routeOrChart: true,
    defaultBackground: 'none',
    fields: [
      { key: 'riders', label: 'Rider photos', group: 'Content' },
      { key: 'names', label: 'Rider first names', group: 'Content' },
      { key: 'squad', label: 'Squad name', group: 'Content', available: (d) => !!d.group?.ride?.squad },
      { key: 'pulling', label: 'Group pulling power', group: 'Content', available: (d) => !!d.group?.ride?.pulling_power },
      ...basics(),
      ...statFields(['stat.distance', 'stat.rating', 'stat.powerChoice', 'stat.xss', 'stat.time', 'stat.speed', 'stat.elevation', 'stat.recovery'], ['stat.time', 'stat.speed', 'stat.elevation', 'stat.recovery']),
      route,
      chart(),
      ...badgeFields('group'),
    ],
  },
  {
    id: 'group-leaderboard',
    type: 'group',
    name: 'Leaderboard',
    description: 'Your group session’s ranking and award winners.',
    component: GroupLeaderboard,
    defaultBackground: 'none',
    defaultAccent: 'gold',
    available: (d) => !!d.group?.session,
    unavailableReason: 'Only for group sessions',
    fields: [
      { key: 'rank', label: 'Your placing', group: 'Content' },
      { key: 'leaderboard', label: 'Leaderboard', group: 'Content' },
      { key: 'awards', label: 'Awards', group: 'Content' },
      ...basics(),
      ...badgeFields('group'),
    ],
  },
  {
    id: 'activity-route',
    type: 'activity',
    name: 'Route',
    description: 'Your route, elevation profile and key numbers.',
    component: ActivityRoute,
    routeOrChart: true,
    defaultBackground: 'none',
    fields: [
      ...basics(),
      route,
      { key: 'elevation', label: 'Elevation profile', group: 'Graphics', available: hasAltitude },
      chart(),
      ...statFields(['stat.distance', 'stat.time', 'stat.elevation', 'stat.xep', 'stat.power', 'stat.speed', 'stat.xss', 'stat.difficulty', 'stat.hr', 'stat.recovery'], ['stat.power', 'stat.speed', 'stat.hr', 'stat.recovery']),
      ...badgeFields('activity'),
    ],
  },
  {
    id: 'activity-poster',
    type: 'activity',
    name: 'Poster',
    description: 'A photo with one big number.',
    component: ActivityPoster,
    defaultBackground: 'photo',
    fields: [
      ...basics(),
      { key: 'hero', label: 'Big number', group: 'Content' },
      route,
      ...statFields(['stat.distance', 'stat.time', 'stat.elevation', 'stat.xep', 'stat.xss', 'stat.speed', 'stat.difficulty', 'stat.recovery'], ['stat.speed', 'stat.difficulty', 'stat.recovery']),
      ...badgeFields('activity'),
    ],
  },
  {
    id: 'activity-analysis',
    type: 'activity',
    name: 'Analysis',
    description: 'A light data sheet: XSS by intensity, focus, difficulty and fuel.',
    component: ActivityAnalysis,
    defaultBackground: 'none',
    defaultAccent: 'blue',
    fields: [
      ...basics(),
      { key: 'xss', label: 'XSS breakdown', group: 'Content', available: (d) => !!d.activity.xss },
      { key: 'focus', label: 'Focus', group: 'Content', available: (d) => !!d.activity.focus },
      { key: 'difficulty', label: 'Difficulty', group: 'Content', available: (d) => !!d.activity.difficulty_stars },
      { key: 'fuel', label: 'Fuel burned', group: 'Content', available: (d) => !!(d.activity.carbs || d.activity.fat) },
      { key: 'signature', label: 'Fitness signature', group: 'Content', default: false, available: (d) => !!d.signature },
      chart(),
      mpa,
      ...statFields(['stat.distance', 'stat.time', 'stat.xep', 'stat.elevation', 'stat.power', 'stat.hr', 'stat.recovery'], ['stat.power', 'stat.hr', 'stat.recovery']),
      ...badgeFields('activity'),
    ],
  },
]

export const TEMPLATES_BY_ID: Record<string, TemplateDef> = Object.fromEntries(TEMPLATES.map((t) => [t.id, t]))

export const TYPE_LABELS: Record<ReportType, string> = {
  breakthrough: 'Breakthrough',
  xfai: 'Forecast AI',
  workout: 'Workout',
  group: 'Group',
  activity: 'Activity',
}

export const TYPE_ORDER: ReportType[] = ['breakthrough', 'xfai', 'workout', 'group', 'activity']

export function templatesFor(type: ReportType): TemplateDef[] {
  return TEMPLATES.filter((t) => t.type === type)
}

export function templateAvailable(def: TemplateDef, data: ReportData): boolean {
  return data.types.includes(def.type) && (!def.available || def.available(data))
}
